import { formatDateTime, getStatusMeta, type MatchProfile } from "../adminConfig";

interface MemberTableProps {
  rows: MatchProfile[];
  loading: boolean;
  error: string;
  onRetry: () => void;
  onSelect: (profile: MatchProfile) => void;
}

function avatarClass(gender: string | null): string {
  if (gender === "男") return "bg-secondary-100 text-secondary-700";
  if (gender === "女") return "bg-accent-100 text-accent-700";
  return "bg-background-200 text-foreground-600";
}

export default function MemberTable({
  rows,
  loading,
  error,
  onRetry,
  onSelect,
}: MemberTableProps) {
  if (error) {
    return (
      <div className="flex flex-col items-center gap-4 rounded-lg border border-background-200 bg-background-50 px-6 py-16 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-100 text-accent-600">
          <i className="ri-error-warning-line text-2xl"></i>
        </span>
        <div>
          <p className="text-sm font-medium text-foreground-900">资料加载失败</p>
          <p className="mt-1 text-xs text-foreground-500">{error}</p>
        </div>
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-refresh-line"></i>
          重新加载
        </button>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[960px] border-collapse">
          <thead>
            <tr className="border-b border-background-200 bg-background-100 text-left text-xs font-medium text-foreground-500">
              <th className="px-4 py-3 font-medium">会员</th>
              <th className="px-4 py-3 font-medium">身高</th>
              <th className="px-4 py-3 font-medium">现居城市</th>
              <th className="px-4 py-3 font-medium">婚姻状况</th>
              <th className="px-4 py-3 font-medium">月收入</th>
              <th className="px-4 py-3 font-medium">手机号</th>
              <th className="px-4 py-3 font-medium">状态</th>
              <th className="px-4 py-3 font-medium">提交时间</th>
              <th className="px-4 py-3 text-right font-medium">操作</th>
            </tr>
          </thead>

          <tbody>
            {loading &&
              Array.from({ length: 6 }).map((_, index) => (
                <tr key={`skeleton-${index}`} className="border-b border-background-100">
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-3">
                      <span className="h-9 w-9 animate-pulse rounded-full bg-background-200"></span>
                      <span className="h-3.5 w-24 animate-pulse rounded bg-background-200"></span>
                    </div>
                  </td>
                  {Array.from({ length: 8 }).map((__, cell) => (
                    <td key={`skeleton-cell-${cell}`} className="px-4 py-3.5">
                      <span className="block h-3.5 w-16 animate-pulse rounded bg-background-200"></span>
                    </td>
                  ))}
                </tr>
              ))}

            {!loading &&
              rows.map((row) => {
                const meta = getStatusMeta(row.status);
                return (
                  <tr
                    key={row.id}
                    className="border-b border-background-100 transition-colors last:border-b-0 hover:bg-background-100/70"
                  >
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-3">
                        <span
                          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-medium ${avatarClass(
                            row.gender,
                          )}`}
                        >
                          {row.name ? row.name.slice(0, 1) : "?"}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-foreground-900">
                            {row.name || "未填写姓名"}
                          </p>
                          <p className="mt-0.5 whitespace-nowrap text-xs text-foreground-400">
                            {row.gender || "—"} ·{" "}
                            {row.age ? `${row.age} 岁` : "年龄未填"}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3.5 text-sm text-foreground-700">
                      {row.height ? `${row.height} cm` : "—"}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-foreground-700">
                      {row.residence || "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3.5 text-sm text-foreground-700">
                      {row.marital_status || "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3.5 text-sm text-foreground-700">
                      {row.income || "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3.5 text-sm text-foreground-700">
                      {row.phone ? (
                        <span className="inline-flex items-center gap-1">
                          {row.phone.includes("*") && (
                            <i className="ri-lock-2-line text-xs text-foreground-400"></i>
                          )}
                          {row.phone}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      <span
                        className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${meta.badge}`}
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`}></span>
                        {meta.label}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3.5 text-xs text-foreground-500">
                      {formatDateTime(row.created_at)}
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <button
                        type="button"
                        onClick={() => onSelect(row)}
                        className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                      >
                        <i className="ri-eye-line"></i>
                        查看
                      </button>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {!loading && rows.length === 0 && (
        <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-inbox-archive-line text-2xl"></i>
          </span>
          <div>
            <p className="text-sm font-medium text-foreground-800">暂无匹配的会员资料</p>
            <p className="mt-1 text-xs text-foreground-400">试试调整筛选条件，或等待客户提交新的资料</p>
          </div>
        </div>
      )}
    </div>
  );
}